import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  SimpleChanges,
  OnChanges,
} from '@angular/core';

export type ScrollButtonPosition = 'side' | 'top' | 'bottom';

/**
  Componente que proporciona funcionalidad de desplazamiento (scroll) personalizable.
  Este componente permite desplazar contenido dentro de un contenedor con controles personalizados
  y emite eventos cuando se realiza un desplazamiento.
**/
@Component({
  selector: 'app-am-scroll',
  templateUrl: './am-scroll.component.html',
  styleUrls: ['./am-scroll.component.scss'],
  standalone: false,
})
export class AmScrollComponent implements OnChanges, AfterViewInit {
  @ViewChild('scrollContainer', { static: true }) scrollContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('scrollWrapper', { static: true }) scrollWrapper!: ElementRef<HTMLDivElement>;

  @Input() height: number | null = null;
  @Input() isMandatoryHeight = false;
  @Input() scrollAmount = 100;
  @Input() buttonPosition: ScrollButtonPosition = 'side';

  canScrollUp = false;
  canScrollDown = false;
  needsScroll = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.height && changes.height.currentValue !== changes.height.previousValue) {
      this.adjustHeight();
      this.updateScrollStatus();
    }
  }

  ngAfterViewInit() {
    this.adjustHeight();
    this.updateScrollStatus();
  }

  scrollUp() {
    this.scrollContainer.nativeElement.scrollBy({ top: -this.scrollAmount, behavior: 'smooth' });
  }

  scrollDown() {
    this.scrollContainer.nativeElement.scrollBy({ top: this.scrollAmount, behavior: 'smooth' });
  }

  onScroll() {
    this.updateScrollStatus();
  }

  private adjustHeight() {
    const contentHeight = this.scrollContainer.nativeElement.scrollHeight;
    let finalHeight: number;

    if (this.height === null) {
      const viewportHeight = window.innerHeight - 100;
      finalHeight = Math.min(contentHeight, viewportHeight);
    } else {
      finalHeight = this.isMandatoryHeight ? this.height : Math.min(contentHeight, this.height);
    }

    this.scrollWrapper.nativeElement.style.height = `${finalHeight}px`;

    const el = this.scrollContainer.nativeElement;
    this.needsScroll = el.scrollHeight > el.clientHeight;
    this.applyPadding();
  }

  private updateScrollStatus() {
    const el = this.scrollContainer.nativeElement;
    this.canScrollUp = el.scrollTop > 0;
    this.canScrollDown = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    this.needsScroll = el.scrollHeight > el.clientHeight;
    this.applyPadding();
  }

  private applyPadding() {
    const el = this.scrollContainer.nativeElement;
    el.style.paddingRight = this.needsScroll && this.buttonPosition === 'side' ? '110px' : '0px';
  }

  @HostListener('window:resize')
  onResize() {
    this.adjustHeight();
  }
}
